'use client';
import { tv } from '../_lib/utils';
import { Container, type ContainerProps } from '../Container';
import { CardSectionHeader, type CardSectionHeaderProps } from '../CardSectionHeader';
import { FilterBar, type FilterBarProps } from '../FilterBar';
import type { PropsWithChildren } from 'react';
import { Card, type CardProps } from '../Card';
import { Configure } from 'react-instantsearch';
import { AlgoliaInfiniteScroll } from '../AlgoliaInfiniteScroll';
import { AlgoliaSelector } from '../AlgoliaSelector';
import { useState } from 'react';
import { AlgoliaSearch } from '../AlgoliaSearch';

export type CardsGridFilterProps = PropsWithChildren<{
	className?: string;
	header?: Omit<CardSectionHeaderProps, 'className'>;
	button?: FilterBarProps['button'];
	items?: Omit<CardProps, 'layout' | 'size'>[];
	hitsPerPage?: number;
	showSearch?: boolean;
	filters?: string;
	filterNavOptions?: string[];
	layout?: '4wide' | '3wide' | '2wide';
	removeCannotRefine?: boolean;
	locale?: string;
	containerSize?: ContainerProps['size'];
	originalOrder?: Map<string, number>;
}>;
const styles = tv({
	slots: {
		items: 'grid gap-responsive-lg',
	},
	variants: {
		layout: {
			'4wide': {
				items: 'sm:grid-cols-2 md:grid-cols-4',
			},
			'3wide': {
				items: 'md:grid-cols-3',
			},
			'2wide': {
				items: 'lg:grid-cols-2',
			},
		},
	},
});

export function CardsGridFilter(props: CardsGridFilterProps) {
	const layout = props.layout ?? '3wide';
	const { items } = styles({ layout });

	const [filtersStates, setFiltersStates] = useState<Record<string, boolean | undefined> | undefined>(
		props.filterNavOptions && props.filterNavOptions.length > 0
			? props.filterNavOptions.reduce((acc, filter) => ({ ...acc, [filter.replace('filters.', '')]: undefined }), {})
			: undefined,
	);

	const showFilters = filtersStates && Object.values(filtersStates).every(value => value === false) ? false : true;

	const currentDate = Math.floor(new Date().getTime() / 1000);

	return (
		<section className={props.className} data-component='CardListing'>
			<Container className='flex flex-col gap-responsive-lg' size={props.containerSize}>
				{props.header && <CardSectionHeader {...props.header} />}

				<AlgoliaSearch indexName='updateddata'>
					<Configure
						hitsPerPage={props.hitsPerPage}
						filters={`${props.filters && props.filters.length > 0 ? `${props.filters} AND ` : ''}publishDate_sort < ${currentDate} AND NOT publishDate_sort:null`}
						attributesToRetrieve={['title', 'slug', 'href', 'publishDate', 'authors', 'articleType', 'featuredImage']}
					/>
					<FilterBar withSearch={props.showSearch}>
						{showFilters && props.filterNavOptions && props.filterNavOptions.length > 0 && (
							<div className='flex flex-col w-full md:flex-row gap-3'>
								{props.filterNavOptions.map(filter => {
									const filterLabel = filterName(filter.replace('filters.', ''));

									return (
										<AlgoliaSelector
											attribute={filter}
											key={filter}
											label={filterLabel ? filterLabel : splitPascalCase(filter.replace('filters.', ''))}
											removeCannotRefine={props.removeCannotRefine}
											setFiltersStates={setFiltersStates}
											index={'updateddata'}
										/>
									);
								})}
							</div>
						)}
					</FilterBar>

					<AlgoliaInfiniteScroll
						className={items()}
						hitComponent={({ hit, index }) => {
							console.log(hit);
							return (
								<Card
									key={index}
									title={hit.title}
									overline={hit.articleType}
									image={hit.featuredImage}
									href={
										(hit._type === 'articles' || hit._highlightResult?._type?.value === 'articles') &&
										['http://localhost:3009', 'https://vocovo-link-enterprise.vercel.app', 'https://link.vocovo.com'].includes(
											process.env.NEXT_PUBLIC_SITE_URL,
										)
											? `${'/news/'}${hit.slug}`
											: hit.href
									}
									meta={[hit.authors?.[0]?.name ?? '', formatDate(hit.publishDate)].filter(Boolean)}
									layout={getCardLayout(layout, index)}
									size={getCardSize(layout, index)}
									nextImageSizes={getCardNextImageSizes(layout)}
								/>
							);
						}}
					/>
				</AlgoliaSearch>
			</Container>
		</section>
	);
}

export function getCardLayout(layout: CardsGridFilterProps['layout'], index: number) {
	if (layout === '2wide') {
		return 'horizontal';
	}

	return 'vertical';
}

export function getCardSize(layout: CardsGridFilterProps['layout'], index: number) {
	if (layout === '2wide') {
		return 'lg';
	} else if (layout === '3wide') {
		return 'md';
	} else if (layout === '4wide') {
		return 'sm';
	}

	return 'lg';
}

export function getCardNextImageSizes(layout: CardsGridFilterProps['layout']) {
	if (layout === '2wide') {
		return '(max-width: 768px) 87vw, (max-width: 1440px) 45vw, 35vw';
	} else if (layout === '3wide') {
		return '(max-width: 768px) 87vw, (max-width: 1440px) 30vw, 25vw';
	} else if (layout === '4wide') {
		return '(max-width: 768px) 87vw, (max-width: 1440px) 25vw, 20vw';
	}

	return '(max-width: 768px) 87vw, (max-width: 1440px) 30vw, 25vw';
}

export function formatDate(dateString: string): string {
	const date = new Date(dateString);
	return new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	}).format(date);
}

export function filterName(filter: string | undefined): string | undefined {
	switch (filter) {
		case 'solutions':
			return 'Solution';
		case 'sectors':
			return 'Sector';
		case 'topics':
			return 'Topic';
		case 'products':
			return 'Product';
		default:
			return undefined;
	}
}

export function splitPascalCase(input: string): string {
	return input
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}
